import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import DataTable, { SortOrder, TableColumn } from 'react-data-table-component';
import { MasterLayout, ToolbarLayout, ToolbarLayoutLeft, ToolbarLayoutRight, MainLayout } from 'common/imports/content-layout';
import { tableHelper } from 'common/helpers/table/table-helper';
import { itLabTableStyle } from 'common/helpers/table/table-style';
import { formatLocalizedDateTime } from 'common/helpers/dateUtils';
import { PagingOptionsModel } from 'common/types/paging-options.model';
import { NotificationHistory, NotificationHistoryFilter, NotificationHistoryResponse, NotificationStatistics } from '../models/notification-history.model';
import { NotificationHistoryService } from '../services/notification-history.service';
import en from '../i18n/en-us.json';
import pt from '../i18n/pt-br.json';
import es from '../i18n/es-es.json';
import { NotificationHistoryFilterDropdown } from './NotificationHistoryFilterDropdown';
import { useNotificationHistoryFilterParams } from '../hooks/use-filter-params';
import NotificationDetailsModal from './NotificationDetailsModal';
import { Tooltip } from '@mui/material';

export default function NotificationHistoryPage() {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);
    i18next.addResourceBundle('es', 'translation', es);

    const { t } = useTranslation();

    const [filter, filterActions] = useNotificationHistoryFilterParams();

    const [tableData, setTableData] = useState<NotificationHistory[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [selectedNotification, setSelectedNotification] = useState<NotificationHistory>();
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [statistics, setStatistics] = useState<NotificationStatistics>({
        totalSent: 0,
        totalFailed: 0,
        totalPending: 0,
        successRate: 0
    });

    const [sorting, setSorting] = useState<PagingOptionsModel>({
        page: 0,
        limit: tableHelper.defaultRowsPerPage
    });

    const fetchData = async () => {
        setIsLoading(true);

        console.log('Fetching data com filters:', filter);
        console.log('Sorting:', sorting);

        const response = await NotificationHistoryService.getNotificationHistory({
            startDate: filter.startDate,
            endDate: filter.endDate,
            channelId: filter.channelId,
            statusId: filter.statusId,
            pageNumber: (sorting.page || 0) + 1,
            pageSize: sorting.limit || tableHelper.defaultRowsPerPage
        });

        console.log('Resposta da API:', response);

        if (response && !response.hasErrors && response.data) {
            console.log('Primeira notificação:', response.data.notifications[0]);
            console.log('Tipo de when:', typeof response.data.notifications[0]?.when);
            console.log('Valor de when:', response.data.notifications[0]?.when);

            setTableData(response.data.notifications);
            setTotalRows(response.data.totalCount);
            setStatistics(response.data.statistics);
        } else {
            console.log('Erro ou sem dados:', response);
        }

        setIsLoading(false);
    };

    const columns: TableColumn<NotificationHistory>[] = [
        {
            name: `${t('notificationHistory.tableHeaders.user')}`,
            selector: (row) => row.userName,
            sortable: true,
            sortField: 'userName',
            width: '200px'
        },
        {
            name: `${t('notificationHistory.tableHeaders.channel')}`,
            selector: (row) => row.channelName,
            sortable: true,
            sortField: 'channelName',
            width: '150px'
        },
        {
            name: `${t('notificationHistory.tableHeaders.type')}`,
            selector: (row) => {
                const typeTranslations: Record<string, string> = {
                    'agentDisconnection': t('notificationHistory.types.agentDisconnection'),
                    'stepExecution': t('notificationHistory.types.stepExecution'),
                    'unexecutedSchedules': t('notificationHistory.types.unexecutedSchedules')
                };
                return typeTranslations[row.type] || row.type;
            },
            sortable: true,
            sortField: 'type',
            width: '200px'
        },
        {
            name: `${t('notificationHistory.tableHeaders.status')}`,
            cell: (row) => {
                const statusText = {
                    0: t('notificationHistory.status.pending'),
                    1: t('notificationHistory.status.sent'),
                    2: t('notificationHistory.status.failed')
                }[row.statusId] || row.statusName;

                return (
                    <span className={`badge badge-light-${getStatusColor(row.statusId)}`}>
                        {statusText}
                    </span>
                );
            },
            sortable: true,
            sortField: 'statusId',
            width: '120px'
        },
        {
            name: `${t('notificationHistory.tableHeaders.date')}`,
            selector: (row) => {
                const date = new Date(row.when);
                return formatLocalizedDateTime(date.toISOString(), 'llll') || '';
            },
            sortable: true,
            sortField: 'when',
            width: '250px'
        },
        {
            name: `${t('notificationHistory.tableHeaders.details')}`,
            cell: (row) => row.custom ? (
                <Tooltip title={t('notificationHistory.viewDetails')} arrow>
                    <button
                        type="button"
                        className="btn btn-icon btn-link"
                        onClick={() => openNotificationDetails(row)}
                    >
                        <i className="bi bi-eye-fill fs-7"></i>
                    </button>
                </Tooltip>
            ) : null,
            width: '100px',
            center: true
        }
    ];

    const getStatusColor = (statusId: number): string => {
        switch (statusId) {
            case 1:
                return 'success';
            case 2:
                return 'danger';
            case 0:
            default:
                return 'warning';
        }
    };

    const openNotificationDetails = (notification: NotificationHistory) => {
        setSelectedNotification(notification);
        setShowDetailsModal(true);
    };

    const handleRowsPerPageChange = async (newRowsPerPage: number) => {
        setIsLoading(true);
        setTableData([]);
        setSorting({
            ...sorting,
            limit: newRowsPerPage
        });
    }

    const handlePageChange = (newPage: number) => {
        setIsLoading(true);
        setSorting({
            ...sorting,
            page: newPage - 1
        });
    };

    const handleListSort = (selectedColumn: TableColumn<NotificationHistory>, sortDirection: SortOrder) => {
        setIsLoading(true);
        setTableData([]);
        setSorting({
            ...sorting,
            sort: selectedColumn.sortField,
            order: sortDirection
        });
    }

    const handleFilterChange = (newFilters: NotificationHistoryFilter) => {
        filterActions.setFilter(newFilters);
    };

    useEffect(() => {
        console.log('useEffect triggered - filter ou sorting mudaram');
        fetchData();
    }, [filter, sorting]);

    return (
        <MasterLayout>
            <ToolbarLayout>
                <ToolbarLayoutLeft />
                <ToolbarLayoutRight>
                    <div className='d-flex align-items-center gap-2'>
                        <NotificationHistoryFilterDropdown
                            filters={filter}
                            onFilterChange={handleFilterChange}
                        />
                    </div>
                </ToolbarLayoutRight>
            </ToolbarLayout>

            <MainLayout>
                <div className="row gy-5 gx-5">
                    <div className="col-xl-3 col-sm-6">
                        <div className="card card-flush">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start">
                                    <span className="fw-semibold fs-2x text-gray-800 lh-1 ls-n2">
                                        {statistics.totalSent}
                                    </span>
                                    <span>
                                        <i className="bi bi-check-circle-fill fs-2x text-success"></i>
                                    </span>
                                </div>
                                <div>
                                    <span className="fw-semibold fs-6 text-gray-400">
                                        {t('notificationHistory.statistics.sent')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-3 col-sm-6">
                        <div className="card card-flush">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start">
                                    <span className="fw-semibold fs-2x text-gray-800 lh-1 ls-n2">
                                        {statistics.totalFailed}
                                    </span>
                                    <span>
                                        <i className="bi bi-x-circle-fill fs-2x text-danger"></i>
                                    </span>
                                </div>
                                <div>
                                    <span className="fw-semibold fs-6 text-gray-400">
                                        {t('notificationHistory.statistics.failed')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-3 col-sm-6">
                        <div className="card card-flush">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start">
                                    <span className="fw-semibold fs-2x text-gray-800 lh-1 ls-n2">
                                        {statistics.totalPending}
                                    </span>
                                    <span>
                                        <i className="bi bi-clock-fill fs-2x text-warning"></i>
                                    </span>
                                </div>
                                <div>
                                    <span className="fw-semibold fs-6 text-gray-400">
                                        {t('notificationHistory.statistics.pending')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-3 col-sm-6">
                        <div className="card card-flush">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start">
                                    <span className="fw-semibold fs-2x text-gray-800 lh-1 ls-n2">
                                        {statistics.successRate}%
                                    </span>
                                    <span>
                                        <i className="bi bi-graph-up-arrow fs-2x text-info"></i>
                                    </span>
                                </div>
                                <div>
                                    <span className="fw-semibold fs-6 text-gray-400">
                                        {t('notificationHistory.statistics.successRate')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mb-8"></div>

                <div className='card card-flush'>
                    <div className='card-body'>
                        <DataTable
                            title={false}
                            persistTableHead
                            columns={columns}
                            data={tableData}
                            noDataComponent={tableHelper.NoDataComponent()}
                            dense
                            highlightOnHover
                            pagination
                            paginationServer
                            paginationComponentOptions={tableHelper.PaginationComponentOptions()}
                            paginationPerPage={tableHelper.defaultRowsPerPage}
                            paginationRowsPerPageOptions={tableHelper.paginationRowsPerPageOptions}
                            paginationTotalRows={totalRows}
                            onChangeRowsPerPage={handleRowsPerPageChange}
                            onChangePage={handlePageChange}
                            onSort={handleListSort}
                            sortServer
                            progressPending={isLoading}
                            progressComponent={tableHelper.Loader()}
                            customStyles={itLabTableStyle}
                        />
                    </div>
                </div>
            </MainLayout>
            <NotificationDetailsModal
                notification={selectedNotification}
                show={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
            />
        </MasterLayout>
    );
}